import React, { useState } from "react";
import { CloseOutlined } from "@ant-design/icons";
import { Popconfirm, Tooltip } from "antd";
import { PlayIcon } from "lucide-react";

import {
  getLinkPreviewAccessibilityLabel,
  getLinkPreviewCardWidth,
  getLinkPreviewHeroAspectRatio,
  getLinkPreviewHref,
  getLinkPreviewLayout,
  getLinkPreviewSiteLabel,
  getRenderableLinkPreviews,
  getTweetAuthorLine,
} from "@fitness-app/client-shared/features/chat/link-preview";
import { type ChatMessage, type LinkPreview } from "@fitness-app/data-models/entities/Chat";
import { cn } from "@fitness-app/utils/src/styles/cn";
import { useTranslation as useInterfaceTranslation } from "react-i18next";

interface LinkPreviewCardProps {
  preview: LinkPreview;
  position?: "left" | "right";
  /** Set for the message's author: hides the card for everyone, like Slack's "Remove preview". */
  onRemove?: (preview: LinkPreview) => void;
}

const THUMBNAIL_SIZE = 72;

/**
 * A link card under a message — what Slack or Messenger show for a pasted URL. The metadata
 * is resolved once by chat-service and stored on the message, so this only lays it out.
 * Images are hot-linked from the page's CDN; one that fails to load simply disappears.
 */
export const LinkPreviewCard = ({ preview, position, onRemove }: LinkPreviewCardProps): React.ReactElement => {
  const { t: tUi } = useInterfaceTranslation();
  const [imageBroken, setImageBroken] = useState(false);
  const [faviconBroken, setFaviconBroken] = useState(false);
  const width = getLinkPreviewCardWidth();
  const layout = imageBroken ? "text" : getLinkPreviewLayout(preview);
  const tweetAuthor = getTweetAuthorLine(preview);
  const siteLabel = getLinkPreviewSiteLabel(preview);

  const renderSiteLine = () => (
    <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500">
      {preview.faviconUrl && !faviconBroken ? (
        <img
          src={preview.faviconUrl}
          alt=""
          width={14}
          height={14}
          loading="lazy"
          className="h-3.5 w-3.5 shrink-0 rounded-sm"
          onError={() => setFaviconBroken(true)}
        />
      ) : null}
      <span className="truncate">{siteLabel}</span>
    </div>
  );

  const renderText = () => {
    if (tweetAuthor) {
      return (
        <>
          {renderSiteLine()}
          <div className="mt-1 flex items-baseline gap-1.5 text-sm">
            <span className="truncate font-semibold text-gray-900">{tweetAuthor.name}</span>
            {tweetAuthor.handle ? <span className="shrink-0 text-gray-500">{tweetAuthor.handle}</span> : null}
          </div>
          {preview.description ? (
            <p className="mt-1 line-clamp-6 whitespace-pre-line text-sm leading-snug text-gray-800">
              {preview.description}
            </p>
          ) : null}
        </>
      );
    }

    return (
      <>
        {renderSiteLine()}
        {preview.title ? (
          <div className="mt-1 line-clamp-2 text-sm font-semibold leading-snug text-gray-900">{preview.title}</div>
        ) : null}
        {preview.description ? (
          <p className="mt-1 line-clamp-3 text-xs leading-snug text-gray-600">{preview.description}</p>
        ) : null}
      </>
    );
  };

  return (
    <div className={cn("flex px-3 py-1", position === "right" ? "justify-end" : "justify-start")}>
      {/* The button sits beside the link, not in it: a control nested in <a> would also open the page. */}
      <div className="group/card relative max-w-full" style={{ width }}>
        <a
          href={getLinkPreviewHref(preview)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={getLinkPreviewAccessibilityLabel(tUi, preview)}
          className="block w-full overflow-hidden rounded-xl border border-gray-200 bg-white text-left no-underline shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
          {layout === "hero" && preview.imageUrl ? (
            <div
              className="relative w-full overflow-hidden bg-gray-100"
              // A ratio, not a height: the card shrinks with a narrow chat pane (`max-w-full`), and
              // a height computed for the full width would leave the image too tall for it.
              style={{ aspectRatio: String(getLinkPreviewHeroAspectRatio(preview)) }}
            >
              <img
                src={preview.imageUrl}
                alt=""
                loading="lazy"
                className="block h-full w-full object-cover"
                onError={() => setImageBroken(true)}
              />
              {preview.kind === "video" ? (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-black/55 backdrop-blur-[2px]">
                    <PlayIcon size={22} className="translate-x-[1px] text-white" fill="currentColor" />
                  </span>
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="flex gap-3 p-3">
            <div className="min-w-0 flex-1">{renderText()}</div>
            {layout === "thumbnail" && preview.imageUrl ? (
              <img
                src={preview.imageUrl}
                alt=""
                width={THUMBNAIL_SIZE}
                height={THUMBNAIL_SIZE}
                loading="lazy"
                className="shrink-0 rounded-lg bg-gray-100 object-cover"
                style={{ width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }}
                onError={() => setImageBroken(true)}
              />
            ) : null}
          </div>
        </a>
        {onRemove ? (
          <Popconfirm
            title="Usunąć podgląd linku?"
            description="Link w wiadomości zostanie, zniknie tylko karta."
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            onConfirm={() => onRemove(preview)}
          >
            <Tooltip title="Usuń podgląd">
              <button
                type="button"
                aria-label="Usuń podgląd linku"
                className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-gray-600 opacity-0 shadow-sm ring-1 ring-gray-200 transition-opacity hover:text-gray-900 focus-visible:opacity-100 group-hover/card:opacity-100"
              >
                <CloseOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
          </Popconfirm>
        ) : null}
      </div>
    </div>
  );
};

interface LinkPreviewsProps {
  message: Pick<ChatMessage, "extra" | "content">;
  position?: "left" | "right";
  onRemove?: (preview: LinkPreview) => void;
}

/** Every card a message carries, in the order the links appear in its text. */
export const LinkPreviews = ({ message, position, onRemove }: LinkPreviewsProps): React.ReactElement | null => {
  const previews = getRenderableLinkPreviews(message);
  if (!previews.length) return null;

  return (
    <>
      {previews.map((preview) => (
        <LinkPreviewCard key={preview.url} preview={preview} position={position} onRemove={onRemove} />
      ))}
    </>
  );
};
